/**
 * The below lint rules are disabled because Pressable relies on building styles at runtime in Forge apps.
 * This implementation is copied from @atlaskit/primitives/src/components/pressable.tsx which has the same
 * pattern. The css prop with runtime-computed values is required for the xcss prop to work correctly.
 * See go/DSP-18766 for more context.
 *
 * @atlaskit/design-system/consistent-css-prop-usage is disabled because this component must import
 * shared style maps (backgroundColorStylesMap, surfaceColorStylesMap, paddingStylesMap) for runtime
 * token resolution. Inlining these maps would create unmaintainable code duplication across Box and
 * Pressable components. This is an architectural requirement for Forge UI Kit's token system.
 */
/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage -- Required for runtime xcss resolution with imported token style maps. See explanation above and go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Required for token map imports. See go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- Required for runtime style computation. See go/DSP-18766 */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// @ts-ignore - jsx is used by pragma
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/no-unused-vars -- jsx is used by pragma
import { css, jsx } from '@emotion/react';
import type { CSSObject, SerializedStyles } from '@emotion/serialize';
import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { adaptEventHandler, useAdaptEventHandler } from '../../utils';
import { xcssValidator } from '../../utils/xcssValidator';
import type { XCSSProp } from '../../utils/xcssValidator';
import type { BackgroundColor, Space } from '../tokens.partial';
import {
	backgroundColorStylesMap,
	borderColorMap,
	borderWidthMap,
	paddingStylesMap,
	positiveSpaceMap,
	resolveTokens,
} from '../tokens.partial';
import { fg } from '@atlaskit/platform-feature-flags';

type NativePressableProps = {
	backgroundColor?: BackgroundColor;
	children?: React.ReactNode;
	isDisabled?: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	padding?: Space;
	paddingBlock?: Space;
	paddingBlockEnd?: Space;
	paddingBlockStart?: Space;
	paddingInline?: Space;
	paddingInlineEnd?: Space;
	paddingInlineStart?: Space;
	testId?: string;
	xcss?: XCSSProp;
};

/**
 * Generates SerializedStyles from xcss style object.
 * We use `increaseSpecificity` to make sure this Emotion beats out Compiled.
 */
function generateXcss(styleObj: CSSObject, increaseSpecificity = true): SerializedStyles {
	const specificity = increaseSpecificity ? '&&' : ('&' as const);
	const resolved = resolveTokens(styleObj);
	return css({ [specificity]: resolved });
}

// Base styles for the Pressable component (matching @atlaskit/primitives)
const baseStyles = css({
	boxSizing: 'border-box',
	appearance: 'none',
	border: 'none',
});
const enabledStyles = css({
	cursor: 'pointer',
});
const disabledStyles = css({
	cursor: 'not-allowed',
});

// This duplicates FocusRing styles from `@atlaskit/focus-ring` (matching @atlaskit/primitives)
const focusRingStyles = css({
	'&:focus, &:focus-visible': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/ensure-design-token-usage
		outlineColor: borderColorMap['color.border.focused'],
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		outlineOffset: positiveSpaceMap['space.025'],
		outlineStyle: 'solid',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		outlineWidth: borderWidthMap['border.width.focused'],
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:focus:not(:focus-visible)': {
		outline: 'none',
		'&:focus-visible': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			outline: `${borderWidthMap['border.width']} solid`,
		},
	},
});

// Internal Pressable component implementation (migrated from @atlaskit/primitives)
const InternalPressable = ({
	children,
	backgroundColor,
	isDisabled,
	onClick,
	padding,
	paddingBlock,
	paddingBlockStart,
	paddingBlockEnd,
	paddingInline,
	paddingInlineStart,
	paddingInlineEnd,
	testId,
	xcss: xcssValue,
}: NativePressableProps) => {
	// Build styles array from SerializedStyles (matching @atlaskit/primitives)
	const styles = [
		baseStyles,
		focusRingStyles,
		isDisabled ? disabledStyles : enabledStyles,
		backgroundColor && backgroundColorStylesMap[backgroundColor],
		padding && paddingStylesMap.padding[padding],
		paddingBlock && paddingStylesMap.paddingBlock[paddingBlock],
		paddingBlockStart && paddingStylesMap.paddingBlockStart[paddingBlockStart],
		paddingBlockEnd && paddingStylesMap.paddingBlockEnd[paddingBlockEnd],
		paddingInline && paddingStylesMap.paddingInline[paddingInline],
		paddingInlineStart && paddingStylesMap.paddingInlineStart[paddingInlineStart],
		paddingInlineEnd && paddingStylesMap.paddingInlineEnd[paddingInlineEnd],
		xcssValue && generateXcss(xcssValue as CSSObject),
	].filter(Boolean);

	return (
		<button
			css={styles}
			data-testid={testId}
			disabled={isDisabled}
			onClick={onClick}
			type="button"
		>
			{children}
		</button>
	);
};

/**
 * A pressable is a primitive for building custom buttons.
 */
export const Pressable = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const {
		backgroundColor,
		isDisabled,
		onClick,
		padding,
		paddingBlock,
		paddingBlockEnd,
		paddingBlockStart,
		paddingInline,
		paddingInlineEnd,
		paddingInlineStart,
		testId,
		xcss,
	} = props.forgeDoc.props as NativePressableProps;

	// Use the appropriate event handler based on analytics feature flag
	const adaptEventHandlerHook = useAdaptEventHandler();
	const eventHandler = fg('forge-ui-performance-analytics-provider')
		? adaptEventHandlerHook(onClick)
		: adaptEventHandler(onClick);

	return (
		<InternalPressable
			backgroundColor={backgroundColor}
			isDisabled={isDisabled}
			onClick={eventHandler}
			padding={padding}
			paddingBlock={paddingBlock}
			paddingBlockEnd={paddingBlockEnd}
			paddingBlockStart={paddingBlockStart}
			paddingInline={paddingInline}
			paddingInlineEnd={paddingInlineEnd}
			paddingInlineStart={paddingInlineStart}
			testId={testId}
			xcss={xcss && xcssValidator(xcss)}
		>
			{props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
		</InternalPressable>
	);
};

